import React, { useEffect, useState } from 'react';
import { FormProvider, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useToast } from '@common/toast';
import BackgroundArea from '@components/profile/profileOverlay/overlays/common/backgroundArea';
import Terms from '@components/profile/profileOverlay/overlays/militaryPreference/terms';
import ExitModal from '@components/profile/profileOverlay/exitModal';
import MilitaryForm from '@components/profile/profileOverlay/overlays/militaryPreference/preferentialWrite/militaryForm';
import VeteranForm from '@components/profile/profileOverlay/overlays/militaryPreference/preferentialWrite/veteranForm';
import DisabilityForm from '@components/profile/profileOverlay/overlays/militaryPreference/preferentialWrite/disabilityForm';
import { useUserInfo } from '@repository/auth/useUserInfo';
import useProfilePreferential, { useSaveProfilePreferential } from '@repository/profiles/useProfilePreferential';
import preferentialFormSchema from '@components/profile/profileOverlay/overlays/militaryPreference/preferentialFormSchema';
import PreferentialYnForm from '@components/profile/profileOverlay/overlays/militaryPreference/preferentialWrite/preferentialYnForm';
import {
  useSaveProfileSensitiveInfoAgree,
  useSensitiveTermsList,
} from '@components/profile/profileOverlay/overlays/militaryPreference/query/useTerms';
import { useMatchProfile } from '@repository/match';
import ProfilePreferentialFormVO from '@domain/vo/profile/ProfilePreferentialFormVO';
import { KeyboardEventUtil } from '@utils/KeyboardEventUtil';

import classnames from 'classnames/bind';
import styles from './militaryPreference.module.scss';

const cx = classnames.bind(styles);

interface IMilitaryPreferenceOverlay {
  visible: boolean;
  onClose: () => void;
}

const preferentialDefaultValues: RecursivePartial<ProfilePreferentialFormVO> = {
  militaryUseYn: false,
  disabilityUseYn: false,
  veteranUseYn: false,

  military: null,
  disability: null,
  veteran: null,
};

const MilitaryPreferenceOverlay = ({ visible, onClose }: IMilitaryPreferenceOverlay) => {
  const Toast = useToast();
  const { data: userInfo } = useUserInfo();
  const { data: profilePreferential, refetch: profilePreferentailRefetch } = useProfilePreferential(
    !!userInfo?.id && visible,
  );

  const { data: termList, refetch: termsListRefetch } = useSensitiveTermsList(!!userInfo?.id);

  const { mutateAsync: saveProfileSensitiveInfoAgree } = useSaveProfileSensitiveInfoAgree();
  const { mutateAsync: saveProfilePreferential } = useSaveProfilePreferential();
  const { refetch: matchProfileRefetch } = useMatchProfile();

  const [exitModalVisible, setExitModalVisible] = useState(false);

  const methods = useForm<ProfilePreferentialFormVO>({
    mode: 'onBlur',
    defaultValues: preferentialDefaultValues as ProfilePreferentialFormVO,
    resolver: yupResolver(preferentialFormSchema) as any,
  });

  const {
    watch,
    reset,
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const militaryUseYn = watch('militaryUseYn');
  const disabilityUseYn = watch('disabilityUseYn');
  const veteranUseYn = watch('veteranUseYn');

  const checkStatus = watch('checkStatus' as any) as boolean[];

  //약관 표시 여부
  const termsVisible = (disabilityUseYn || veteranUseYn) && termList?.length;
  const isDisabledSaveBtn = termsVisible && checkStatus?.filter((item) => item).length !== termList?.length;

  const onCloseOverlay = () => {
    if (isDirty) {
      setExitModalVisible(true);
      return;
    }

    onClose();
  };

  const onSubmit: SubmitHandler<ProfilePreferentialFormVO> = async (data) => {
    if ((disabilityUseYn || veteranUseYn) && termList?.length) {
      //민감정보 약관 동의들 동의 저장
      const termSns = termList.map((item) => item.sn);
      await saveProfileSensitiveInfoAgree(termSns);
      await termsListRefetch();
    }

    saveProfilePreferential(data)
      .then((result) => {
        if (result) {
          Toast({ type: 'success', iconType: 'info', content: '성공적으로 저장되었습니다.' });
          profilePreferentailRefetch();
          matchProfileRefetch();
        } else {
          Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
        }
      })
      .catch(() => {
        Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
      });
  };

  const onSubmitError: SubmitErrorHandler<ProfilePreferentialFormVO> = () => {
    Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
  };

  useEffect(() => {
    if (profilePreferential) {
      reset({ ...new ProfilePreferentialFormVO(profilePreferential) });
    } else {
      reset(preferentialDefaultValues);
    }
  }, [profilePreferential]);

  return (
    <>
      <BackgroundArea visible={visible} onClickArea={onCloseOverlay} />
      <FormProvider {...methods}>
        <form
          onSubmit={(v) => handleSubmit(onSubmit, onSubmitError)(v)}
          onKeyDown={KeyboardEventUtil.onCheckKeyDownPreventEnterExceptTextarea}
        >
          <div className={cx('overlayArea', { on: visible })}>
            <div className={cx('overlayInner')}>
              <div className={cx('overlayTitleArea')}>
                <strong className={cx('overlayTitle')}>병역 · 취업 우대 사항이 있으면 입력해 주세요.</strong>
              </div>

              <div className={cx('scrollArea')}>
                <div className={cx('scrollInner')}>
                  <div className={cx('scrollWrap')}>
                    <PreferentialYnForm />

                    {(militaryUseYn || disabilityUseYn || veteranUseYn) && (
                      <div className={cx('requiredTextArea')}>
                        <span className={cx('requiredText')}>필수 입력</span>
                      </div>
                    )}

                    {/* 병역 영역 */}
                    {militaryUseYn && <MilitaryForm />}

                    {/* 장애 영역 */}
                    {disabilityUseYn && <DisabilityForm />}

                    {/* 보훈 영역 */}
                    {veteranUseYn && <VeteranForm />}

                    {/* 약관 영역 */}
                    {!!termsVisible && <Terms termList={termList} />}
                  </div>
                </div>
              </div>

              <div className={cx('btnArea')}>
                <a className={cx('btn', 'cancel')} role="button" onClick={onCloseOverlay}>
                  나가기
                </a>
                <button
                  className={cx('btn', 'confirm', { disabled: !!isDisabledSaveBtn })}
                  type="submit"
                  disabled={!!isDisabledSaveBtn}
                >
                  저장
                </button>
              </div>
            </div>

            {/* 나가기 모달 영역 */}
            <ExitModal visible={exitModalVisible} onClose={() => setExitModalVisible(false)} onExit={onClose} />
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default MilitaryPreferenceOverlay;
